// Settings → Held station actions (DECISIONS D-024): what stations sent that needs a
// person — from a revoked device, an unexpected device, a fact that couldn't be recorded.
// The station has moved on; a manager applies or discards each one here. The list leads
// with when it happened, and marks an action that reached the server more than a day
// later (the key risk: an old fact landing in today's stock, review UI3A-10).

import type { HeldCommandSummary } from '@enp/domain/api';
import {
  Badge,
  cn,
  EmptyState,
  PageHeader,
  StatusBadge,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@enp/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronRight, Inbox, PartyPopper } from 'lucide-react';
import type { ReactNode } from 'react';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { HOLD_REASON, HOLD_STATUS, commandLabel, deviceOf } from '../../../../stations/labels.js';
import { AccountDate } from '../../../../shell/account-date.js';
import {
  ActionsListSkeleton,
  DeviceName,
  HappenedCell,
  NoDeviceAccess,
  SettingsBreadcrumb,
  SettingsFrame,
  useCanManageDevices,
} from '../../../../stations/page-parts.js';
import { holdsQuery, holdsSearchSchema } from '../../../../stations/queries.js';

export const Route = createFileRoute('/_app/settings/held-actions/')({
  validateSearch: holdsSearchSchema,
  component: HeldActionsPage,
});

function FilterLink({
  status,
  current,
  children,
}: {
  status: 'open' | 'resolved';
  current: 'open' | 'resolved';
  children: ReactNode;
}) {
  const active = status === current;
  return (
    <Link
      to="/settings/held-actions"
      search={status === 'open' ? {} : { status }}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'inline-flex h-control items-center gap-2 rounded-md px-3 text-body font-medium',
        active ? 'bg-surface-raised text-fg shadow-xs' : 'text-muted hover:text-fg',
      )}
    >
      {children}
    </Link>
  );
}

function HoldRow({ hold, showStatus }: { hold: HeldCommandSummary; showStatus: boolean }) {
  const status = HOLD_STATUS[hold.status];
  const device = deviceOf(hold);
  const staff = hold.staff?.display_name ?? 'Unknown';
  return (
    <TableRow className="relative hover:bg-surface-hover">
      <TableCell>
        <HappenedCell occurredAt={hold.occurred_at} receivedAt={hold.received_at} />
      </TableCell>
      <TableCell>
        <span className="flex flex-col leading-tight">
          <Link
            to="/settings/held-actions/$holdId"
            params={{ holdId: hold.id }}
            className="font-medium text-fg after:absolute after:inset-0 hover:underline"
          >
            {commandLabel(hold.type)}
          </Link>
          {hold.physical && <span className="text-xs text-muted">Happened in the shop</span>}
          {/* Below lg the staff column folds in here. */}
          <span className="text-xs text-muted lg:hidden">By {staff}</span>
        </span>
      </TableCell>
      <TableCell>
        <DeviceName code={device.code} station={device.station} />
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {hold.staff?.display_name ?? <span className="text-muted">Unknown</span>}
      </TableCell>
      <TableCell>{HOLD_REASON[hold.reason].label}</TableCell>
      {showStatus && (
        <TableCell>
          {/* What was decided, when and by whom (UI3A-210). */}
          <span className="flex flex-col items-start gap-1 leading-tight">
            <StatusBadge status={status.status}>{status.label}</StatusBadge>
            {hold.resolved_at && (
              <span className="text-xs text-muted">
                <AccountDate value={hold.resolved_at} />
                {hold.resolved_by && `, by ${hold.resolved_by.name}`}
              </span>
            )}
          </span>
        </TableCell>
      )}
      <TableCell className="w-8">
        <ChevronRight aria-hidden className="size-4 text-muted" />
      </TableCell>
    </TableRow>
  );
}

function HeldActionsPage() {
  useDocumentTitle('Held station actions');
  const search = Route.useSearch();
  const status = search.status ?? 'open';
  const canManage = useCanManageDevices();
  const list = useQuery({ ...holdsQuery(status), enabled: canManage === true });
  const items = list.data?.items ?? [];
  // Every waiting one is "Waiting": the column only earns its place for resolved ones.
  const showStatus = status === 'resolved';

  return (
    <SettingsFrame>
      <SettingsBreadcrumb page="Held station actions" />
      <PageHeader
        title="Held station actions"
        description="Actions a station sent that need a person before they count. The station has already moved on, and nothing in them is lost: apply each one, or discard it with a note."
      />
      {canManage === false ? (
        <NoDeviceAccess />
      ) : (
        <>
          <nav
            aria-label="Filter held actions"
            className="mb-4 inline-flex w-fit gap-1 rounded-lg bg-surface-sunken p-1"
          >
            <FilterLink status="open" current={status}>
              Waiting
              {list.data && (
                <Badge tone={list.data.open_count > 0 ? 'warning' : 'neutral'}>
                  {list.data.open_count}
                </Badge>
              )}
            </FilterLink>
            <FilterLink status="resolved" current={status}>
              Resolved
            </FilterLink>
          </nav>
          {list.isError ? (
            <ErrorPanel
              error={list.error}
              titleAs="h2"
              what="The held actions"
              onRetry={() => void list.refetch()}
            />
          ) : !list.data ? (
            <ActionsListSkeleton label="Loading held actions" decided={showStatus} />
          ) : items.length === 0 ? (
            status === 'open' ? (
              <EmptyState
                icon={PartyPopper}
                tone="success"
                title="Nothing is waiting"
                description="Every action the stations sent went through. When one needs a person, it appears here."
              />
            ) : (
              <EmptyState
                icon={Inbox}
                title="Nothing resolved yet"
                description="Actions you apply or discard are kept here, with who decided and why."
              />
            )
          ) : (
            <>
              <Table>
                <TableCaption className="sr-only">
                  {status === 'open' ? 'Held actions waiting' : 'Resolved held actions'}
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Happened</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead className="hidden lg:table-cell">Staff</TableHead>
                    <TableHead>{showStatus ? 'Why it was held' : 'Why it’s held'}</TableHead>
                    {showStatus && <TableHead>Decided</TableHead>}
                    <TableHead>
                      <span className="sr-only">Open</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((h) => (
                    <HoldRow key={h.id} hold={h} showStatus={showStatus} />
                  ))}
                </TableBody>
              </Table>
              {list.data.truncated && (
                <p className="mt-3 text-sm text-muted">
                  Showing the newest {items.length}. Resolve these to see older ones.
                </p>
              )}
            </>
          )}
        </>
      )}
    </SettingsFrame>
  );
}
